'use client';

// Bloco P · E1 · Migrado pra react-hook-form + zodResolver (validação client) preservando
// a lógica de 3 modos (password / otp-email / otp-code).

import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiArrowLeft, FiEye, FiEyeOff, FiMail } from 'react-icons/fi';
import { z } from 'zod';

import { finishLoginAfterMfa, requestOtp, signInWithPassword, verifyOtp } from './actions';
import { OtpVerifyCard } from './otp-input';

import { AuthSplitHero } from '@/app/(auth)/_components/auth-split-hero';
import { BrandIcon } from '@/components/brand-icon';
import { createSupabaseBrowserClient } from '@/lib/supabase/browser';

type Mode = 'password' | 'otp-email' | 'otp-code' | 'mfa-challenge';

// stagger fade-in (substitui as classes animate-element animate-delay-XXX do easemize)
const fade = {
  initial: { opacity: 0, y: 16, filter: 'blur(6px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
};

function delay(i: number) {
  return { transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] as const, delay: i * 0.08 } };
}

function GlassInputWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-border bg-ink/5 focus-within:border-violet/70 focus-within:bg-violet/10 rounded-2xl border backdrop-blur-sm transition-colors">
      {children}
    </div>
  );
}

/**
 * Brand lockup oficial — proporções do brandbook §wordmark-display:
 * - gap = altura × 24/132 (≈ 0.182×)
 * - font-size = altura do ícone (1:1)
 * - weight 700, letter-spacing -0.045em, line-height 1
 * - align-items: center
 */
function BrandLockup({ size = 28 }: { size?: number }) {
  return (
    <span className="inline-flex items-center" style={{ gap: `${(size * 24) / 132}px` }}>
      <BrandIcon size={size} className="rounded-[22%]" alt="Trakd" />
      <span
        className="text-ink"
        style={{
          fontSize: `${size}px`,
          fontWeight: 700,
          letterSpacing: '-0.045em',
          lineHeight: 1,
        }}
      >
        Trakd
      </span>
    </span>
  );
}

const passwordSchema = z.object({
  email: z.string().trim().email('Email inválido'),
  password: z.string().min(6, 'Senha precisa ter ao menos 6 caracteres'),
  rememberMe: z.boolean().optional(),
});

type PasswordValues = z.infer<typeof passwordSchema>;

const otpEmailSchema = z.object({
  email: z.string().trim().email('Email inválido'),
});

type OtpEmailValues = z.infer<typeof otpEmailSchema>;

export function LoginForm() {
  const params = useSearchParams();
  // Fluxo signup-via-invite: ?redirect=/aceitar-convite/<token> força OTP + allowSignup.
  // Convidado novo cria conta sem passar pela Stripe — entra no workspace existente.
  const redirectTarget = params.get('redirect');
  const isInviteFlow =
    typeof redirectTarget === 'string' && redirectTarget.startsWith('/aceitar-convite/');
  const initialMode: Mode = isInviteFlow || params.get('modo') === 'otp' ? 'otp-email' : 'password';

  const [mode, setMode] = useState<Mode>(initialMode);
  const [emailForOtp, setEmailForOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  const passwordForm = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { email: '', password: '', rememberMe: true },
  });

  const otpEmailForm = useForm<OtpEmailValues>({
    resolver: zodResolver(otpEmailSchema),
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  function go(to: string) {
    const target = params.get('redirect') ?? to;
    window.location.assign(target);
  }

  async function onSubmitPassword(values: PasswordValues): Promise<void> {
    setError(null);
    // "Manter conectado": grava a preferência ANTES do login, pra request do
    // signIn já chegar com ela: os adaptadores de cookie do Supabase (server
    // e middleware) leem trakd_keep e, desmarcada, emitem cookies de sessão
    // (morrem ao fechar o navegador) em vez de persistentes.
    document.cookie = `trakd_keep=${values.rememberMe ? '1' : '0'}; path=/; max-age=34560000; samesite=lax`;
    const fd = new FormData();
    fd.set('email', values.email);
    fd.set('password', values.password);
    const res = await signInWithPassword(fd);
    if (!res.ok) {
      setError(res.error ?? 'Erro ao entrar');
      return;
    }
    if (res.needsMfa && res.mfaFactorId) {
      setEmailForOtp(values.email);
      setMfaFactorId(res.mfaFactorId);
      setMfaCode('');
      setMode('mfa-challenge');
      return;
    }
    go(res.redirectTo ?? '/dashboard');
  }

  async function onSubmitOtpEmail(values: OtpEmailValues): Promise<void> {
    setError(null);
    const fd = new FormData();
    fd.set('email', values.email);
    if (isInviteFlow) fd.set('allow_signup', '1');
    const res = await requestOtp(fd);
    if (!res.ok) {
      setError(res.error ?? 'Erro ao enviar código');
      return;
    }
    setEmailForOtp(values.email);
    setOtp('');
    setResendIn(45);
    setMode('otp-code');
  }

  async function verifyCode(code: string): Promise<void> {
    setError(null);
    setVerifying(true);
    try {
      const fd = new FormData();
      fd.set('email', emailForOtp);
      fd.set('token', code);
      const res = await verifyOtp(fd);
      if (!res.ok) {
        setError(res.error ?? 'Código inválido');
        return;
      }
      // OTP por email é um segundo fator natural (ownership da caixa) — não
      // pedimos TOTP em cima. Quem entra por senha continua passando pelo MFA
      // challenge no signInWithPassword.
      go(res.redirectTo ?? '/dashboard');
    } finally {
      setVerifying(false);
    }
  }

  /**
   * Verifica o código TOTP do app authenticator. Roda no browser client porque
   * a API mfa.challenge/verify do Supabase exige a sessão ativa do user, e a
   * sessão AAL1 que acabou de ser criada pelo password/OTP já está nos cookies.
   * Após verify, chama finishLoginAfterMfa (server) pra limpar chat history e
   * resolver o redirect baseado em onboarding state.
   */
  async function verifyMfaCode(code: string): Promise<void> {
    if (!mfaFactorId) {
      setError('Sessão MFA perdida. Volta pro login e tenta de novo.');
      return;
    }
    setError(null);
    setVerifying(true);
    try {
      const supabase = createSupabaseBrowserClient();
      const challengeRes = await supabase.auth.mfa.challenge({ factorId: mfaFactorId });
      if (challengeRes.error || !challengeRes.data) {
        setError(challengeRes.error?.message ?? 'Falha ao iniciar o challenge.');
        setMfaCode('');
        return;
      }
      const verifyRes = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challengeRes.data.id,
        code,
      });
      if (verifyRes.error) {
        setError('Código inválido ou expirado. Confere o app authenticator.');
        setMfaCode('');
        return;
      }

      // mfa.verify OK aqui = sessão já é AAL2 nos cookies do browser. O
      // finishLoginAfterMfa só limpa chat history e decide redirect (dashboard
      // vs onboarding). Se ele falhar por qualquer motivo (RLS, query, etc),
      // ainda assim o user está autenticado — manda pro /dashboard, que tem
      // gate próprio pra redirecionar pro onboarding se faltar steps.
      try {
        const finish = await finishLoginAfterMfa();
        go(finish.redirectTo ?? '/dashboard');
      } catch (err) {
        console.error('[verifyMfaCode] finishLoginAfterMfa threw:', err);
        go('/dashboard');
      }
    } catch (err) {
      console.error('[verifyMfaCode]', err);
      setError('Erro inesperado. Tenta de novo.');
      setMfaCode('');
    } finally {
      setVerifying(false);
    }
  }

  async function cancelMfa(): Promise<void> {
    // User desistiu do challenge — derruba a sessão AAL1 e volta pro login.
    const supabase = createSupabaseBrowserClient();
    await supabase.auth.signOut();
    setMfaFactorId(null);
    setMfaCode('');
    setMode('password');
    setError(null);
  }

  async function resendOtp(): Promise<void> {
    if (resendIn > 0) return;
    setError(null);
    const fd = new FormData();
    fd.set('email', emailForOtp);
    if (isInviteFlow) fd.set('allow_signup', '1');
    const res = await requestOtp(fd);
    if (!res.ok) {
      setError(res.error ?? 'Erro ao reenviar');
      return;
    }
    setResendIn(45);
  }

  // OTP step: card cheio (estilo thanh/otp-verify) — não usa layout split
  if (mode === 'otp-code') {
    return (
      <OtpVerifyCard
        email={emailForOtp}
        value={otp}
        onChange={setOtp}
        onSubmit={verifyCode}
        onResend={resendOtp}
        onBack={() => {
          setMode('password');
          setOtp('');
          setError(null);
        }}
        resendIn={resendIn}
        loading={verifying}
        error={error}
      />
    );
  }

  // MFA challenge: mesmo card visual do OTP, mas captura código TOTP do app
  // authenticator do user (Google Authenticator, 1Password, Authy etc).
  if (mode === 'mfa-challenge') {
    return (
      <OtpVerifyCard
        mode="mfa"
        email={emailForOtp}
        value={mfaCode}
        onChange={setMfaCode}
        onSubmit={verifyMfaCode}
        onResend={() => {
          /* no-op: TOTP é offline, sempre disponível no app */
        }}
        onBack={cancelMfa}
        resendIn={0}
        loading={verifying}
        error={error}
      />
    );
  }

  const passwordPending = passwordForm.formState.isSubmitting;
  const otpEmailPending = otpEmailForm.formState.isSubmitting;

  // Senha + OTP-email: layout split 100dvh (estilo easemize)
  return (
    <div className="flex h-[100dvh] w-[100dvw] flex-col md:flex-row">
      {/* Painel esquerdo · form */}
      <section className="flex flex-1 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {mode === 'password' ? (
              <motion.div
                key="password"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-6"
              >
                <motion.div {...fade} initial="initial" animate="animate" {...delay(0)}>
                  <BrandLockup size={28} />
                </motion.div>

                <motion.h1
                  {...fade}
                  initial="initial"
                  animate="animate"
                  {...delay(1)}
                  className="text-ink text-4xl leading-tight font-semibold tracking-tighter md:text-5xl"
                >
                  Bem-vindo <span className="text-trakd-brand font-light">de volta</span>
                </motion.h1>

                <motion.p
                  {...fade}
                  initial="initial"
                  animate="animate"
                  {...delay(2)}
                  className="text-ink-muted"
                >
                  Acesse sua conta e continue otimizando suas campanhas com tracking server-side.
                </motion.p>

                <form
                  className="space-y-5"
                  onSubmit={passwordForm.handleSubmit(onSubmitPassword)}
                  noValidate
                >
                  <motion.div {...fade} initial="initial" animate="animate" {...delay(3)}>
                    <label className="text-ink-muted text-sm font-medium">E-mail</label>
                    <GlassInputWrapper>
                      <input
                        type="email"
                        autoComplete="email"
                        autoFocus
                        placeholder="seu@email.com"
                        disabled={passwordPending}
                        aria-invalid={!!passwordForm.formState.errors.email}
                        className="text-ink placeholder:text-ink-faint w-full rounded-2xl bg-transparent p-4 text-sm focus:outline-none"
                        {...passwordForm.register('email')}
                      />
                    </GlassInputWrapper>
                    {passwordForm.formState.errors.email?.message && (
                      <p className="text-error mt-1 text-xs">
                        {passwordForm.formState.errors.email.message}
                      </p>
                    )}
                  </motion.div>

                  <motion.div {...fade} initial="initial" animate="animate" {...delay(4)}>
                    <label className="text-ink-muted text-sm font-medium">Senha</label>
                    <GlassInputWrapper>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          placeholder="Sua senha"
                          disabled={passwordPending}
                          aria-invalid={!!passwordForm.formState.errors.password}
                          className="text-ink placeholder:text-ink-faint w-full rounded-2xl bg-transparent p-4 pr-12 text-sm focus:outline-none"
                          {...passwordForm.register('password')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          className="absolute inset-y-0 right-3 flex items-center"
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <FiEyeOff className="text-ink-subtle hover:text-ink h-5 w-5 transition-colors" />
                          ) : (
                            <FiEye className="text-ink-subtle hover:text-ink h-5 w-5 transition-colors" />
                          )}
                        </button>
                      </div>
                    </GlassInputWrapper>
                    {passwordForm.formState.errors.password?.message && (
                      <p className="text-error mt-1 text-xs">
                        {passwordForm.formState.errors.password.message}
                      </p>
                    )}
                  </motion.div>

                  <motion.div
                    {...fade}
                    initial="initial"
                    animate="animate"
                    {...delay(5)}
                    className="flex items-center justify-between text-sm"
                  >
                    <label className="flex cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        {...passwordForm.register('rememberMe')}
                      />
                      <span className="text-ink/90">Manter conectado</span>
                    </label>
                    <Link
                      href="/recuperar-senha"
                      className="text-trakd-brand font-medium transition-opacity hover:opacity-80"
                    >
                      Esqueci minha senha
                    </Link>
                  </motion.div>

                  {error && (
                    <p
                      className="text-error rounded-xl bg-red-500/10 px-3 py-2 text-sm"
                      role="alert"
                    >
                      {error}
                    </p>
                  )}

                  <motion.button
                    {...fade}
                    initial="initial"
                    animate="animate"
                    {...delay(6)}
                    type="submit"
                    disabled={passwordPending}
                    className="bg-trakd-fill text-on-brand shadow-trakd-glow-violet w-full rounded-2xl py-4 font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {passwordPending ? 'Entrando…' : 'Entrar'}
                  </motion.button>
                </form>

                <motion.div
                  {...fade}
                  initial="initial"
                  animate="animate"
                  {...delay(7)}
                  className="relative flex items-center justify-center"
                >
                  <span className="border-border w-full border-t"></span>
                  <span className="text-ink-faint bg-bg absolute px-4 text-xs tracking-wider uppercase">
                    ou
                  </span>
                </motion.div>

                <motion.button
                  {...fade}
                  initial="initial"
                  animate="animate"
                  {...delay(8)}
                  type="button"
                  onClick={() => {
                    setMode('otp-email');
                    setError(null);
                  }}
                  className="border-border hover:bg-surface text-ink flex w-full items-center justify-center gap-3 rounded-2xl border py-4 text-sm transition-colors"
                >
                  <FiMail className="h-4 w-4" /> Entrar com código por e-mail
                </motion.button>

                <motion.p
                  {...fade}
                  initial="initial"
                  animate="animate"
                  {...delay(9)}
                  className="text-ink-muted text-center text-sm"
                >
                  Ainda não tem conta?{' '}
                  <a
                    href="/#pricing"
                    className="text-trakd-brand transition-opacity hover:opacity-80"
                  >
                    Ver planos
                  </a>
                </motion.p>
              </motion.div>
            ) : (
              <motion.div
                key="otp-email"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-6"
              >
                <motion.div {...fade} initial="initial" animate="animate" {...delay(0)}>
                  <BrandLockup size={28} />
                </motion.div>

                <motion.h1
                  {...fade}
                  initial="initial"
                  animate="animate"
                  {...delay(1)}
                  className="text-ink text-4xl leading-tight font-semibold tracking-tighter md:text-5xl"
                >
                  Entrar com <span className="text-trakd-brand font-light">código</span>
                </motion.h1>

                <motion.p
                  {...fade}
                  initial="initial"
                  animate="animate"
                  {...delay(2)}
                  className="text-ink-muted"
                >
                  Digite seu e-mail e enviamos um código de 6 dígitos.
                </motion.p>

                <form
                  className="space-y-5"
                  onSubmit={otpEmailForm.handleSubmit(onSubmitOtpEmail)}
                  noValidate
                >
                  <motion.div {...fade} initial="initial" animate="animate" {...delay(3)}>
                    <label className="text-ink-muted text-sm font-medium">E-mail</label>
                    <GlassInputWrapper>
                      <input
                        type="email"
                        autoFocus
                        autoComplete="email"
                        placeholder="seu@email.com"
                        disabled={otpEmailPending}
                        aria-invalid={!!otpEmailForm.formState.errors.email}
                        className="text-ink placeholder:text-ink-faint w-full rounded-2xl bg-transparent p-4 text-sm focus:outline-none"
                        {...otpEmailForm.register('email')}
                      />
                    </GlassInputWrapper>
                    {otpEmailForm.formState.errors.email?.message && (
                      <p className="text-error mt-1 text-xs">
                        {otpEmailForm.formState.errors.email.message}
                      </p>
                    )}
                  </motion.div>

                  {error && (
                    <p
                      className="text-error rounded-xl bg-red-500/10 px-3 py-2 text-sm"
                      role="alert"
                    >
                      {error}
                    </p>
                  )}

                  <motion.button
                    {...fade}
                    initial="initial"
                    animate="animate"
                    {...delay(4)}
                    type="submit"
                    disabled={otpEmailPending}
                    className="bg-trakd-fill text-on-brand shadow-trakd-glow-violet w-full rounded-2xl py-4 font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
                  >
                    {otpEmailPending ? 'Enviando…' : 'Enviar código'}
                  </motion.button>

                  <motion.button
                    {...fade}
                    initial="initial"
                    animate="animate"
                    {...delay(5)}
                    type="button"
                    onClick={() => {
                      setMode('password');
                      setError(null);
                    }}
                    className="text-ink-subtle hover:text-ink mt-1 inline-flex w-full items-center justify-center gap-2 text-sm transition-colors"
                  >
                    <FiArrowLeft className="h-3.5 w-3.5" /> Voltar ao login com senha
                  </motion.button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <AuthSplitHero />
    </div>
  );
}
